import React, {createContext, useContext, useEffect, useMemo, useRef} from 'react';
import clsx from 'clsx';

interface TabProps extends React.HTMLAttributes<HTMLElement> {
  element?: keyof JSX.IntrinsicElements;
  selectedValue: string;
  children: React.ReactNode;
}

interface TabItemProps extends React.HTMLAttributes<HTMLElement> {
  element?: keyof JSX.IntrinsicElements;
  value: string;
  children: React.ReactNode;
  activeClassName?: string;
}

interface TabContextType {
  selectedValue: string;
}

const TabContext = createContext<TabContextType | null>(null);

function Tab({element = 'ul', selectedValue, children, ...props}: TabProps) {
  const contextValue = useMemo(
    () => ({
      selectedValue,
    }),
    [selectedValue]
  );

  return React.createElement(
    element,
    {
      ...props,
    },
    <TabContext.Provider value={contextValue}>
      {React.Children.map(children, (child) => (React.isValidElement(child) ? child : null))}
    </TabContext.Provider>
  );
}

function Item({element = 'li', value, className, children, activeClassName = '', ...props}: TabItemProps) {
  const context = useContext(TabContext);
  const ref = useRef<HTMLElement | null>(null);

  const isSelected = context?.selectedValue === value;
  useEffect(() => {
    if (isSelected) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [isSelected]);

  return React.createElement(
    element,
    {
      ref,
      className: clsx(className, isSelected && activeClassName),
      'data-tab-key': value,
      ...props,
    },
    children
  );
}

Tab.Item = Item;
export default Tab;
